import { BseStateNameDto, PublicEventStatusDto, SourcingEventStatus } from "../../../autogen/bff-api";

export type BucketKey =
  | "arbeid"
  | "kunngjort"
  | "evaluering"
  | "tildeling"
  | "fullfort";

export type BucketBadgeKey =
  | "anskArbeid"
  | "anskKunngjort"
  | "anskEvaluering"
  | "anskTildeling"
  | "anskFullfort";

export const STATUS_BUCKET: Record<SourcingEventStatus, BucketKey> = {
  [SourcingEventStatus.Draft]: "arbeid",
  [SourcingEventStatus.PublicationPending]: "arbeid",
  [SourcingEventStatus.PublicationFailed]: "arbeid",
  [SourcingEventStatus.Published]: "kunngjort",
  [SourcingEventStatus.Evaluation]: "evaluering",
  [SourcingEventStatus.AwardDecision]: "tildeling",
  [SourcingEventStatus.Awarded]: "tildeling",
  [SourcingEventStatus.Closed]: "fullfort",
  [SourcingEventStatus.Cancelled]: "fullfort",
  [SourcingEventStatus.Completed]: "fullfort",
};

/**
 * The same five buckets for older (V1) events. A bucket link opens a list of both kinds, so both
 * kinds have to be bucketed the same way — and counted the same way; otherwise selecting a status
 * silently drops every V1 event, or a badge disagrees with the list it opens. A deadline-passed BSE
 * event ("Svarfrist utløpt") is stored as Published, so it belongs under Announced.
 */
export const V1_STATUS_BUCKET: Record<BseStateNameDto, BucketKey> = {
  [BseStateNameDto.Draft]: "arbeid",
  [BseStateNameDto.Published]: "kunngjort",
  [BseStateNameDto.Completed]: "evaluering",
  [BseStateNameDto.Awarded]: "tildeling",
  [BseStateNameDto.Cancelled]: "fullfort",
  [BseStateNameDto.Closed]: "fullfort",
};

export const BUCKET_META: Record<BucketKey, { labelKey: string; badgeKey: BucketBadgeKey }> = {
  arbeid: { labelKey: "In progress", badgeKey: "anskArbeid" },
  kunngjort: { labelKey: "Announced", badgeKey: "anskKunngjort" },
  evaluering: { labelKey: "Under evaluation", badgeKey: "anskEvaluering" },
  tildeling: { labelKey: "Award and standstill", badgeKey: "anskTildeling" },
  fullfort: { labelKey: "Completed", badgeKey: "anskFullfort" },
};

export const STATUSES_BY_BUCKET: Record<BucketKey, SourcingEventStatus[]> = (
  Object.entries(STATUS_BUCKET) as [SourcingEventStatus, BucketKey][]
).reduce(
  (acc, [status, bucket]) => {
    acc[bucket].push(status);
    return acc;
  },
  Object.fromEntries(
    (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => [bucket, [] as SourcingEventStatus[]]),
  ) as Record<BucketKey, SourcingEventStatus[]>,
);

export const V1_STATUSES_BY_BUCKET: Record<BucketKey, BseStateNameDto[]> = (
  Object.entries(V1_STATUS_BUCKET) as [BseStateNameDto, BucketKey][]
).reduce(
  (acc, [status, bucket]) => {
    acc[bucket].push(status);
    return acc;
  },
  Object.fromEntries(
    (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => [bucket, [] as BseStateNameDto[]]),
  ) as Record<BucketKey, BseStateNameDto[]>,
);

export const statusesForBuckets = (buckets: BucketKey[]): (SourcingEventStatus | BseStateNameDto)[] =>
  buckets.flatMap((bucket) => [...STATUSES_BY_BUCKET[bucket], ...V1_STATUSES_BY_BUCKET[bucket]]);

export const parseStatusBuckets = (raw: string): BucketKey[] => {
  const buckets = raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => (isBucketKey(token) ? token : V1_STATUS_BUCKET[token as BseStateNameDto]))
    .filter((bucket): bucket is BucketKey => bucket != null);
  return Array.from(new Set(buckets));
};

export interface EventStatusCount {
  bseStatus?: BseStateNameDto | null;
  publicStatus?: PublicEventStatusDto | null;
  count: number;
}

/**
 * Folds the BFF's per-status counts into badge totals. Each count names the status of one kind, so
 * it is looked up in that kind's map — and a status the BFF knows but this build does not is skipped
 * rather than throwing.
 */
export const countBadgesByStatus = (counts: EventStatusCount[]): Partial<Record<BucketBadgeKey, number>> => {
  const badges: Partial<Record<BucketBadgeKey, number>> = {};
  for (const { bseStatus, publicStatus, count } of counts) {
    const bucket = publicStatus
      ? STATUS_BUCKET[publicStatus as unknown as SourcingEventStatus]
      : bseStatus
        ? V1_STATUS_BUCKET[bseStatus]
        : undefined;
    const badgeKey = bucket ? BUCKET_META[bucket]?.badgeKey : undefined;
    if (!badgeKey) continue;
    badges[badgeKey] = (badges[badgeKey] ?? 0) + count;
  }
  return badges;
};

export const bucketQuery = (bucket: BucketKey): string => `status=${bucket}`;

export const isBucketKey = (slug: string | null): slug is BucketKey =>
  slug != null && Object.prototype.hasOwnProperty.call(BUCKET_META, slug);
